///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import { saveAs } from "file-saver";
import {
    COD_VAMAL,
    CONDITIE_LIVRARE,
    LOCALITATE,
    MOD_TRANSPORT,
    NATURA_TRANZACTIEI,
    TARA_DEST_EXP,
    TARA_ORIGINE,
    TARA_ORIGINE_EU,
    UNITATE
} from "../databases/VersionsIntrastat";
// import { getCountyCode } from "./CountryService";
import { getXMLTimestamp } from "./DateService";
import {
    ITEM,
    asString,
    normalizeAmount,
    normalizeCn8,
    normalizeCountry,
    normalizeDeliveryTerms,
    normalizePartnerVat,
    normalizeQuantity,
    normalizeTransactionNature,
    normalizeTransportMode,
    splitPartnerVat,
    toDeclarantVatNr,
    toXmlInteger,
    validateHeaderData,
    validateItemArray,
    SEVERITY,
} from "./IntrastatRulesService";

//-----------------------------------------------------------------------------------------
//* \brief
//? Thrown when the data handed to the generator cannot produce a declaration the official
//? INTRASTAT application would accept. Carries the list of findings so the caller can
//? show them to the user instead of writing a broken file to disk.
//-----------------------------------------------------------------------------------------
export class IntrastatXmlError extends Error {
    constructor(findings) {
        super('Datele nu respectă schema INTRASTAT')
        this.name = 'IntrastatXmlError'
        this.findings = findings
    }
}

const encodeXmlEntities = (str) => {
    return asString(str).replace(/[&<>"']/g, (char) => {
        switch (char) {
        case '&':
            return '&amp;';
        case '<':
            return '&lt;';
        case '>':
            return '&gt;';
        case '"':
            return '&quot;';
        case "'":
            return '&apos;';
        default:
            return char;
        }
    });
}

//-----------------------------------------------------------------------------------------
//* \brief
//? xsd:token collapses whitespace - do it ourselves so the file matches what the parser
//? will read back and so stray newlines/tabs from Excel never reach the XML.
//-----------------------------------------------------------------------------------------
const token = (str) => asString(str).replace(/\s+/g, ' ').trim()

const top =
`<?xml version="1.0" encoding="UTF-8" ?>
`

// 0 - new 1 - Revised
const main_start = (type) => {
    const declType = type === 'achizitii' ? 'Arrival' : 'Dispatch'
    const toReturn = [
`<InsNew${declType} SchemaVersion="1.0" xmlns="http://www.intrastat.ro/xml/InsSchema">
`,
`<InsRevised${declType} SchemaVersion="1.0" xmlns="http://www.intrastat.ro/xml/InsSchema">
`
]
    return toReturn
}
// 0 - new 1 - Revised
const main_end = (type) => {
    const declType = type === 'achizitii' ? 'Arrival' : 'Dispatch'
    const toReturn = [
`</InsNew${declType}>
`,
`</InsRevised${declType}>
`
]
    return toReturn
}

const versions =
`   <InsCodeVersions>
        <CountryVer>${TARA_ORIGINE}</CountryVer>
        <EuCountryVer>${TARA_ORIGINE_EU}</EuCountryVer>
        <CnVer>${COD_VAMAL}</CnVer>
        <ModeOfTransportVer>${MOD_TRANSPORT}</ModeOfTransportVer>
        <DeliveryTermsVer>${CONDITIE_LIVRARE}</DeliveryTermsVer>
        <NatureOfTransactionAVer>${NATURA_TRANZACTIEI}</NatureOfTransactionAVer>
        <NatureOfTransactionBVer>${NATURA_TRANZACTIEI}</NatureOfTransactionBVer>
        <CountyVer>${TARA_DEST_EXP}</CountyVer>
        <LocalityVer>${LOCALITATE}</LocalityVer>
        <UnitVer>${UNITATE}</UnitVer>
    </InsCodeVersions>
`;

const header = (data) => {
    const toReturnStart =
`   <InsDeclarationHeader>
        <VatNr>${data.compVat}</VatNr>
        <FirmName>${encodeXmlEntities(token(data.compName))}</FirmName>
        <RefPeriod>${data.refPeriod}</RefPeriod>
        <CreateDt>${getXMLTimestamp()}</CreateDt>
        <ContactPerson>
            <LastName>${encodeXmlEntities(token(data.fName))}</LastName>
            <FirstName>${encodeXmlEntities(token(data.lName))}</FirstName>
            <Email>${encodeXmlEntities(token(data.email))}</Email>
            <Phone>${encodeXmlEntities(token(data.phone))}</Phone>
            <Position>${encodeXmlEntities(token(data.position))}</Position>
        </ContactPerson>
`;
    //? <DTPAddress> is deliberately left empty.
    //?
    //? The XSD marks Street, LocalityCode and CountyCode as mandatory inside AddressType,
    //? but LocalityCode and CountyCode are nomenclature backed - that is why LocalityVer
    //? and CountyVer are declared in <InsCodeVersions>. The official INTRASTAT
    //? application resolves them against those nomenclatures on import, and an element
    //? that is present but empty resolves to null and is dereferenced, killing the import
    //? with java.lang.NullPointerException at
    //?   com.atkosoft.intrastat.app.core.ASUtils.validateDeclarationNomenclatures(ASUtils.java:1096)
    //?
    //? With no children at all there is nothing to resolve and the application accepts
    //? the declaration - which is what it has always done for files from this generator.
    //? Do NOT "fix" this against the schema: emitting the mandatory children broke every
    //? third party declaration. The app is the authority here, not the XSD.
    //?
    //? Filling them in for real needs a locality nomenclature we do not ship (RoCities
    //? only has counties), so it would still leave LocalityCode empty.
    const tertPart =
`        <DTPDetails>
            <VatNr>${data.tertVat}</VatNr>
            <FirmName>${encodeXmlEntities(token(data.tertName))}</FirmName>
            <DTPAddress>
            </DTPAddress>
         </DTPDetails>
`;
    const toReturnFinal =
`   </InsDeclarationHeader>
`;
    if(data.tert) return (toReturnStart + tertPart + toReturnFinal)
    return (toReturnStart + toReturnFinal)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Normalizes one flat item array in place-free fashion. The table already normalized the
//? values, but the generator repeats it so that no code path (re-generating an old
//? declaration, an aggregated row, a value typed straight into the table) can slip past.
//?
//? This is also where the four PositiveLongType fields are rounded, and the only place
//? that rounds at all. By the time an item gets here its duplicates have already been
//? summed, so the whole number written to the XML is the rounded total and not a sum of
//? rounded parts - see the rounding policy in IntrastatRulesService.
//-----------------------------------------------------------------------------------------
const normalizeItem = (item, type) => {
    const out = [...item]
    out[ITEM.CN8]                = normalizeCn8(item[ITEM.CN8]).value
    out[ITEM.INVOICE_VALUE]      = toXmlInteger(normalizeAmount(item[ITEM.INVOICE_VALUE]).value)
    out[ITEM.NET_MASS]           = toXmlInteger(normalizeQuantity(item[ITEM.NET_MASS]).value)
    out[ITEM.DELIVERY_TERMS]     = normalizeDeliveryTerms(item[ITEM.DELIVERY_TERMS]).value
    out[ITEM.COUNTRY_ORIGIN]     = normalizeCountry(item[ITEM.COUNTRY_ORIGIN]).value
    out[ITEM.COUNTRY_PARTNER]    = normalizeCountry(item[ITEM.COUNTRY_PARTNER]).value
    out[ITEM.STAT_VALUE]         = toXmlInteger(normalizeAmount(item[ITEM.STAT_VALUE]).value)
    out[ITEM.TRANSACTION_NATURE] = normalizeTransactionNature(item[ITEM.TRANSACTION_NATURE]).value
    out[ITEM.TRANSPORT_MODE]     = normalizeTransportMode(item[ITEM.TRANSPORT_MODE]).value
    out[ITEM.SUPPL_UNIT_CODE]    = asString(item[ITEM.SUPPL_UNIT_CODE]).trim()
    out[ITEM.SUPPL_UNIT_QTY]     = toXmlInteger(normalizeQuantity(item[ITEM.SUPPL_UNIT_QTY]).value)
    if (type !== 'achizitii') {
        out[ITEM.PARTNER_VAT] = normalizePartnerVat(item[ITEM.PARTNER_VAT]).value
    }
    return out
}

const items = (data) => {
    const { itemArray, type, isValStat } = data
    if(itemArray.length < 1) return ''
    let finalString = ``
    // header of item
    const start = (idx) => type === 'achizitii' ?
`   <InsArrivalItem OrderNr="${idx+1}">
` :
`   <InsDispatchItem OrderNr="${idx+1}">
`;
    // footer of item
    const end = type === 'achizitii' ?
`   </InsArrivalItem>
` :
`   </InsDispatchItem>
`;
    // content of item
    for(let i = 0; i < itemArray.length; i++) {
        const item = itemArray[i]
        //? Nature of transaction is always a two level code ("4.2"): A is the category,
        //? B the full code. Splitting on indexOf('.') used to produce an empty mandatory
        //? <NatureOfTransactionACode> whenever the cell held a single digit.
        const natureParts = asString(item[ITEM.TRANSACTION_NATURE]).match(/^(\d)\.(\d)$/)
        const transNatureA = natureParts[1]
        const transNatureB = `${natureParts[1]}.${natureParts[2]}`

        let helperString = start(i)
        helperString +=
`        <Cn8Code>${item[ITEM.CN8]}</Cn8Code>
        <InvoiceValue>${item[ITEM.INVOICE_VALUE]}</InvoiceValue>
`;
        if(isValStat) helperString +=
`        <StatisticalValue>${item[ITEM.STAT_VALUE]}</StatisticalValue>
`;
        helperString +=
`        <NetMass>${item[ITEM.NET_MASS]}</NetMass>
        <NatureOfTransactionACode>${transNatureA}</NatureOfTransactionACode>
        <NatureOfTransactionBCode>${transNatureB}</NatureOfTransactionBCode>
        <DeliveryTermsCode>${item[ITEM.DELIVERY_TERMS]}</DeliveryTermsCode>
        <ModeOfTransportCode>${item[ITEM.TRANSPORT_MODE]}</ModeOfTransportCode>
        <CountryOfOrigin>${item[ITEM.COUNTRY_ORIGIN]}</CountryOfOrigin>
`;
        //? InsSupplUnitsInfo is only written when the CN8 code requires a supplementary
        //? unit AND a positive quantity exists - QtyInSupplUnits is a PositiveLongType,
        //? so <QtyInSupplUnits>0</QtyInSupplUnits> makes the whole file invalid.
        const supplCode = asString(item[ITEM.SUPPL_UNIT_CODE]).trim()
        const supplQty  = asString(item[ITEM.SUPPL_UNIT_QTY]).trim()
        if(supplCode !== '' && supplQty !== '' && parseInt(supplQty, 10) > 0) helperString +=
`        <InsSupplUnitsInfo>
            <SupplUnitCode>${encodeXmlEntities(supplCode)}</SupplUnitCode>
            <QtyInSupplUnits>${parseInt(supplQty, 10)}</QtyInSupplUnits>
        </InsSupplUnitsInfo>
`;
        if(type === 'achizitii') helperString +=
`        <CountryOfConsignment>${item[ITEM.COUNTRY_PARTNER]}</CountryOfConsignment>
`;
        else {
            const partner = splitPartnerVat(item[ITEM.PARTNER_VAT])
            helperString +=
`        <CountryOfDestination>${item[ITEM.COUNTRY_PARTNER]}</CountryOfDestination>
        <PartnerCountryCode>${partner[0]}</PartnerCountryCode>
        <PartnerVatNr>${partner[1]}</PartnerVatNr>
`;
        }
        helperString += end
        finalString += helperString
    }
    return finalString
}

// Order:
// top
// main_start
// ->versions
// ->header
// ->items
// main_end

//-----------------------------------------------------------------------------------------
//* \brief
//? Builds the declaration XML. Every value is normalized once more and the whole payload
//? is validated against the official rules first - if anything would produce a file the
//? INTRASTAT application rejects, an IntrastatXmlError is thrown and nothing is written.
//-----------------------------------------------------------------------------------------
export const getXMLGeneratedData = (data) => {
    const type = data.type
    const normalized = {
        ...data,
        compVat:   toDeclarantVatNr(data.compVat),
        tertVat:   toDeclarantVatNr(data.tertVat),
        itemArray: (data.itemArray || []).map(item => normalizeItem(item, type)),
    }

    const findings = validateHeaderData(normalized)
    normalized.itemArray.forEach((item, index) => {
        validateItemArray(item, { type, isValStat: data.isValStat }).forEach(finding => {
            if (finding.severity !== SEVERITY.ERROR) return
            findings.push({ ...finding, row: index + 1 })
        })
    })
    if (findings.some(f => f.severity === SEVERITY.ERROR)) {
        throw new IntrastatXmlError(findings.filter(f => f.severity === SEVERITY.ERROR))
    }

    const result = top +
        main_start(type)[data.reviz ? 1 : 0] +
        versions +
        header(normalized) +
        items(normalized) +
        main_end(type)[data.reviz ? 1 : 0]
    return result
}

export const downloadXmlFomString = (string, title = 'programintrastat_XML_generat') => {
    const blob = new Blob([string], {type: 'text/xml'})
    saveAs(blob, `${title}.xml`)
}
